import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {ContactUsBanner} from '@/components/common/banner/ContactUsBanner'
import {ContentLayoutWithSidebar} from '@/components/common/content/ContentLayoutWithSidebar'
import {Section} from '@/components/common/content/Section'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {usePreferences} from '@/components/context/Preferences'
import {HeaderMainButtonProps} from '@/components/header/HeaderMainButton'
import {Page} from '@/components/Page'
import {getQuestionsAndAnswersContent} from '@/content/questionsAndAnswers'
import {useTranslation} from '@/i18n'
import {RecentArticleCard} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {DynamicPriceSheets, renderDynamicPricesExpressions} from '@/utils/pricing'
import {QuestionsAndAnswersContentType} from '@/utils/questions-and-answers/constants'
import {
  QuestionsAndAnswersAuthor,
  QuestionsAndAnswersCategory,
  QuestionsAndAnswersItem,
  QuestionsAndAnswersRelatedQuestion,
} from '@/utils/questions-and-answers/types'

import {QuestionsAndAnswersAnswer} from '../QuestionsAndAnswersAnswer'
import {QuestionsAndAnswersRelatedQuestions} from '../QuestionsAndAnswersRelatedQuestions'
import {QuestionsAndAnswersResources} from '../QuestionsAndAnswersResources'

type QuestionsAndAnswersQuestionPageProps = {
  type: QuestionsAndAnswersContentType
  category: QuestionsAndAnswersCategory
  question: QuestionsAndAnswersItem
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  relatedArticles: RecentArticleCard[]
  author: QuestionsAndAnswersAuthor
  // Only the trademark Q&A quotes prices; an answer without its entries shows the raw expression.
  prices?: DynamicPriceSheets
  mainCtaButton?: HeaderMainButtonProps
  // Built in the route, so the same array drives the visible trail and the BreadcrumbList.
  breadcrumbTrail: BreadcrumbStep[]
}

export const QuestionsAndAnswersQuestionPage: FC<QuestionsAndAnswersQuestionPageProps> = ({
  type,
  category,
  question,
  relatedQuestions,
  relatedArticles,
  author,
  prices = {},
  mainCtaButton,
  breadcrumbTrail,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {currency, isPreferencesPending} = usePreferences()

  const {relatedArticlesTitle, helpCenterCategories} = getQuestionsAndAnswersContent(type)

  const {name, answer: answerSource, relatedServices} = question

  const answer = renderDynamicPricesExpressions(
    answerSource,
    language,
    prices,
    isPreferencesPending ? undefined : currency,
  )

  return (
    <Page helpCenterCategories={helpCenterCategories} mainCtaButton={mainCtaButton}>
      <Section hasPaddingTop>
        <ContentLayoutWithSidebar
          breadcrumbTrail={breadcrumbTrail}
          hideCurrentBreadcrumb
          layoutTitle={name}
          relatedServices={relatedServices}
        >
          <QuestionsAndAnswersAnswer answer={answer} author={author} date={question.dateCreated} />
        </ContentLayoutWithSidebar>
      </Section>
      {relatedQuestions.length > 0 && (
        <Section hasPaddingTop isHighlighted>
          <QuestionsAndAnswersRelatedQuestions type={type} relatedQuestions={relatedQuestions} />
        </Section>
      )}
      {relatedArticles.length > 0 && (
        <Section hasPaddingTop>
          <MostRecentArticles
            articlesTitle={t(relatedArticlesTitle)}
            articles={relatedArticles}
            trackedEvent={{
              name: 'RelatedBlogs-QA',
              parameters: {type, category: category.name, question: name},
            }}
          />
        </Section>
      )}
      <Section hasPaddingTop>
        <QuestionsAndAnswersResources type={type} />
      </Section>
      <Section hasPaddingTop hasPaddingBottom>
        <ContactUsBanner
          trackedEventParameters={{
            position: 'Contact us banner - QA',
            type,
            category: category.name,
            question: name,
          }}
        />
      </Section>
    </Page>
  )
}
